/**
 * @hidden
 * @module geo/bus
 * @private
 */
import bus from 'post-robot';
import {Store} from 'redux';
import {Observable, Subject} from 'rxjs';
import {TypeKeyActions} from '../actions/types.key.action';
import {TypeKeyActionsProxy} from '../actions/types.key.actions.proxy';
// import { EventsStore } from './store/events.store';
import {TypeKeyUtils, TypeKeyUtilsProxy} from '../actions/types.key.utils';
import {GEO_API_PROXY_READY} from '../payload/proxy/GEO_API_PROXY_READY';
import {AppStore} from '../store/app';

export interface ResponseBus<P> {
  data: P;
}

/**
 * Class for comunication with proxy.
 * Several app could be registered with unique identifier.
 * @private
 */
export class BusService {
  private registeredApp: IHashApp = {};

  /**
   * Initialize Bus service with config
   * @param config object of configuration. Available options are post-robot
   * config option (https://github.com/krakenjs/post-robot)
   */
  public init(config: any) {

    Object.assign(bus.CONFIG, config);
  }

  /**
   * Register new app to bus service
   * @param uidApp
   * @param configApp
   */
  public registerNewApp(uidApp: string, configApp: any) {
    const promise = new Promise<GEO_API_PROXY_READY>((resolve, reject) => {
      bus.once(
          TypeKeyActions.GEO_API_PROXY_READY + '_' + uidApp, (event: any) => {
            this.registeredApp[uidApp].inited = true;
            this.registeredApp[uidApp].isReady = event.data;
            resolve(event.data);
          });
    });

    const domain = (configApp.geo && configApp.geo.domain) ? configApp.geo.domain : '*';
    this.registeredApp[uidApp] = new BusApp(uidApp, promise, domain);
  }

  /**
   * get BusApp for registered App
   * @param {string} uid
   * @returns {BusApp}
   */
  public getRegisteredApp(uid: string) {
    if (!this.registeredApp[uid]) {
      throw Error('No app register in Bus Service for uid ' + uid);
    }
    return this.registeredApp[uid];
  }

  /**
   * Save the iframe in wich proxy bus is set
   * @param c iframe DOM element
   */
  public setClientReceiver(c: string|HTMLIFrameElement, uidApp: string) {
    const registeredApp = this.registeredApp[uidApp];

    if (typeof c === 'string') {
      const el = document.getElementById(c) as HTMLIFrameElement;
      if (el !== null) {
        registeredApp.receiver = el;
      }
    } else {
      registeredApp.receiver = c;
    }
    if (registeredApp.receiver == null) {
      throw new Error('registred App has no receiver set');
    }

    registeredApp.listener = bus.listener({
      domain: registeredApp.domain,
      window: registeredApp.receiver.contentWindow
    });
    registeredApp.sender = bus.client({
      domain: registeredApp.domain,
      window: registeredApp.receiver.contentWindow
    });
  }

  /**
   * @internal
   * @param {Store<AppStore>} storeApp
   * @param {string} uidApp
   */
  public bindToStore(storeApp: Store<AppStore>, uidApp: string) {
    for (const item in TypeKeyActions) {
      if (isNaN(Number(item)) && item !== 'GEO_API_PROXY_READY') {
        const value = TypeKeyActions[item] + '_' + uidApp;
        this.registeredApp[uidApp].listener.on(value, (result: any) => {
          const action = {type: item, payload: result.data};
          storeApp.dispatch(action);
        });
      }
    }

    for (const item in TypeKeyActionsProxy) {
      if (isNaN(Number(item)) && item !== 'OTHER_ACTION') {
        const value = TypeKeyActionsProxy[item] + '_' + uidApp;

        this.registeredApp[uidApp].listener.on(value, (result: any) => {
          const action = {type: item, payload: result.data};

          storeApp.dispatch(action);
        });
      }
    }

    for (const item in TypeKeyUtils) {
      if (isNaN(Number(item))) {
        const value = TypeKeyUtils[item] + '_' + uidApp;
        this.registeredApp[uidApp].listener.on(value, (result: any) => {
          const action = {type: item, payload: result.data};
          this.registeredApp[uidApp].onNext(action);
        });
      }
    }

    for (const item in TypeKeyUtilsProxy) {
      if (isNaN(Number(item))) {
        const value = TypeKeyUtilsProxy[item] + '_' + uidApp;
        this.registeredApp[uidApp].listener.on(value, (result: any) => {
          const action = {type: item, payload: result.data};
          this.registeredApp[uidApp].onNext(action);
        });
      }
    }
  }

  /**
   * Is bus ready for communicate with proxy of app
   * @param uidApp unique identifier for app
   */
  public getReady(bus: BusService, uidApp: string):
      Promise<GEO_API_PROXY_READY> {
    return bus.registeredApp[uidApp].promiseInit;
  }

  /**
   * Send a action message to proxy and return a promise.
   * @param type Message type
   * @param message payload
   */
  public send<ResponseType>(type: string, message: any, uidApp: string):
      Promise<ResponseType> {
    return this.getRegisteredApp(uidApp).send(type, message);
  }

  /**
   * @param type
   * @param message
   * @param uidApp unique identifier for app
   */
  public sendP<ResponseType>(type: string, message: any, uidApp: string):
      Promise<ResponseBus<ResponseType>> {
    return this.getRegisteredApp(uidApp).sendP(type, message);
  }
}

export const geoBus = new BusService();

/**
 * @internal
 * @private
 */
export class BusApp {
  public uid: string;
  public listener: any;
  public sender: any;
  public receiver?: HTMLIFrameElement;
  public promiseInit: Promise<GEO_API_PROXY_READY>;
  public domain: string = '*';
  public isReady: GEO_API_PROXY_READY = {'ready': false};
  public inited: boolean = false;
  private utilsListener: Subject<any>;

  /**
   * @param {string} uid
   * @param {Promise<GEO_API_PROXY_READY>} promise
   * @param {string} domain
   */
  constructor(
      uid: string, promise: Promise<GEO_API_PROXY_READY>, domain: string) {
    this.uid = uid;
    this.promiseInit = promise;
    this.domain = domain;
    this.utilsListener = new Subject();
  }

  public send<ResponseType>(type: string, message: any): Promise<ResponseType> {
    return this.sender.send(type, message);
  }

  public sendP<ResponseType>(type: string, message: any):
      Promise<ResponseBus<ResponseType>> {
    return this.sender.send(type, message);
  }

  /**
   * Callback on response for message with no action on store that notify
   * busapp subject with result
   * See `getListener` to subscribe
   *
   * @private
   */
  public onNext(data: any) {
    this.utilsListener.next(data);
  }

  /**
   * return an observable on a type of message
   */
  public getListener(type: string): Observable<any> {
    return this.utilsListener.filter((value) => value.type === type)
        .pluck('payload');
  }
}

/**
 * @private
 */
interface IHashApp {
  [uid: string]: BusApp;
}
